import { useEffect, useMemo, useState } from "react";
import { useAppState } from "../context/AppStateContext";
import { useGeoDirectory } from "../context/GeoDirectoryContext";
import { fetchLeadsTable, type LeadTableRow } from "../api/leadsTableApi";
import { normalizeGeoLabel } from "../utils/geoLabel";
import type { TgRegistrationDistrictRow } from "../data/geoDirectoryStore";
import type { Application } from "../types";

// ─── types ────────────────────────────────────────────────────────────────────

type Counts = { leads: number; applicants: number; beneficiaries: number };
type VillageNode  = { id: string; name: string; counts: Counts };
type MandalNode   = { id: string; name: string; villages: VillageNode[] };
type DistrictNode = { id: string; name: string; mandals: MandalNode[] };

function norm(s: string | null | undefined) {
  return normalizeGeoLabel(s ?? "");
}

function buildTree(
  districts: TgRegistrationDistrictRow[],
  leads: LeadTableRow[],
  apps: Application[],
): DistrictNode[] {
  return districts.map((d) => ({
    id: d.districtKey,
    name: d.registrationDistrictName,
    mandals: d.mandals.map((m) => ({
      id: `${d.districtKey}__${m.mandalName}`,
      name: m.mandalName,
      villages: m.villages.map((v) => {
        const vn = norm(v.name);
        const mn = norm(m.mandalName);
        const dk = d.districtKey.toLowerCase();
        const here = apps.filter(
          (a) =>
            norm(a.village) === vn &&
            norm(a.mandal) === mn &&
            String(a.district).toLowerCase() === dk,
        );
        return {
          id: `${d.districtKey}__${m.mandalName}__${v.name}`,
          name: v.name,
          counts: {
            leads: leads.filter(
              (l) =>
                norm(l.village) === vn &&
                norm(l.mandal) === mn &&
                (l.district ?? "").toLowerCase().replace(/\s+/g, "_") === dk,
            ).length,
            applicants: here.length,
            beneficiaries: here.filter((a) => a.status === "subcontractor_linked").length,
          },
        };
      }),
    })),
  }));
}

function sumCounts(items: { counts: Counts }[]): Counts {
  return items.reduce(
    (a, x) => ({ leads: a.leads + x.counts.leads, applicants: a.applicants + x.counts.applicants, beneficiaries: a.beneficiaries + x.counts.beneficiaries }),
    { leads: 0, applicants: 0, beneficiaries: 0 },
  );
}

// ─── CSS (ONLY for the org-chart connector lines) ─────────────────────────────

const CONNECTOR_CSS = `
/* row that holds all sibling nodes */
.oct-row {
  display: inline-flex;
  align-items: flex-start;
}

/* wrapper for each node inside a row */
.oct-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 12px;
}

/*
  Connector bar above each node.
  Negative margin extends it into the gap between columns so
  adjacent border-tops meet seamlessly.
*/
.oct-conn {
  display: flex;
  align-items: stretch;       /* lets oct-vl fill full height */
  height: 30px;
  width: calc(100% + 24px);   /* 24px = 2 × 12px column padding */
  margin: 0 -12px;
}

/* left half of horizontal bar */
.oct-hl { flex: 1; border-top: 2px solid #475569; }

/* vertical drop from the bar down to the node */
.oct-vl { width: 2px; background: #475569; flex-shrink: 0; }

/* right half of horizontal bar */
.oct-hr { flex: 1; border-top: 2px solid #475569; }

/* first child: no left bar */
.oct-col:first-child .oct-hl { border-top: none; }

/* last child: no right bar */
.oct-col:last-child  .oct-hr { border-top: none; }

/* single child: no bars at all, just the vertical drop */
.oct-col:only-child  .oct-hl,
.oct-col:only-child  .oct-hr { border-top: none; }

/* short stem going DOWN from a parent node before the children row */
.oct-stem {
  width: 2px;
  height: 28px;
  background: #475569;
  margin: 0 auto;
  display: block;
}
`;

// ─── node box ─────────────────────────────────────────────────────────────────

type Level = "state" | "district" | "mandal" | "village";

const LEVEL_STYLE: Record<Level, { border: string; bg: string; activeBg: string; nameColor: string; iconBg: string }> = {
  state:    { border: "#1e3a5f", bg: "#1e3a5f",   activeBg: "#1e3a5f",   nameColor: "#fff",    iconBg: "#2e4f7f" },
  district: { border: "#2563eb", bg: "#ffffff",   activeBg: "#dbeafe",   nameColor: "#1e3a8a", iconBg: "#eff6ff" },
  mandal:   { border: "#7c3aed", bg: "#ffffff",   activeBg: "#ede9fe",   nameColor: "#4c1d95", iconBg: "#f5f3ff" },
  village:  { border: "#16a34a", bg: "#f0fdf4",   activeBg: "#dcfce7",   nameColor: "#14532d", iconBg: "#dcfce7" },
};

function NodeBox({
  name,
  icon,
  sub,
  counts,
  level,
  active,
  onClick,
}: {
  name: string;
  icon: string;
  sub?: string;
  counts: Counts;
  level: Level;
  active?: boolean;
  onClick?: () => void;
}) {
  const ls = LEVEL_STYLE[level];

  return (
    <div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 5,
        padding: "10px 12px",
        border: `2px solid ${ls.border}`,
        borderRadius: 10,
        background: active ? ls.activeBg : ls.bg,
        cursor: onClick ? "pointer" : "default",
        minWidth: level === "state" ? 140 : 110,
        maxWidth: 160,
        textAlign: "center",
        boxShadow: active
          ? `0 0 0 3px ${ls.border}44, 0 4px 12px rgba(0,0,0,.12)`
          : "0 1px 4px rgba(0,0,0,.08)",
        userSelect: "none",
        transition: "background .15s, box-shadow .15s",
        position: "relative",
      }}
    >
      {/* active indicator dot */}
      {active && (
        <span style={{
          position: "absolute", top: -8, right: -8,
          width: 16, height: 16, borderRadius: "50%",
          background: ls.border, border: "2px solid #fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 9, color: "#fff", fontWeight: 900,
        }}>
          ✓
        </span>
      )}

      {/* icon */}
      <div style={{
        width: 34, height: 34, borderRadius: 8,
        background: ls.iconBg,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: level === "state" ? "1.3rem" : "1.1rem",
        border: level === "state" ? "1px solid #2e4f7f" : `1px solid ${ls.border}44`,
      }}>
        {icon}
      </div>

      {/* name */}
      <div style={{
        fontSize: level === "state" ? "0.875rem" : "0.78rem",
        fontWeight: 700,
        color: ls.nameColor,
        lineHeight: 1.3,
        wordBreak: "break-word",
      }}>
        {name}
      </div>

      {/* sub-label */}
      {sub && (
        <div style={{ fontSize: "0.68rem", color: "#94a3b8", marginTop: -2 }}>{sub}</div>
      )}

      {/* counts */}
      <div style={{ display: "flex", flexDirection: "column", gap: 3, width: "100%", marginTop: 2 }}>
        <CountRow label="Leads"         value={counts.leads}         color="#1d4ed8" />
        <CountRow label="Applicants"    value={counts.applicants}    color="#6d28d9" />
        <CountRow label="Beneficiaries" value={counts.beneficiaries} color="#15803d" />
      </div>
    </div>
  );
}

function CountRow({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "2px 7px", borderRadius: 5,
      background: color,
      fontSize: "0.68rem", fontWeight: 600, color: "#fff",
    }}>
      <span>{label}</span>
      <span style={{ fontWeight: 800 }}>{value}</span>
    </div>
  );
}

// ─── AddLeadModal is now in components/AddLeadModal.tsx ──────────────────────
import { AddLeadModal, type AddLeadTarget } from "../components/AddLeadModal";

// ─── org-chart row with connector lines ───────────────────────────────────────

function OrgRow<T extends { id: string }>({
  items,
  selectedId,
  renderNode,
}: {
  items: T[];
  selectedId?: string;
  renderNode: (item: T, active: boolean) => React.ReactNode;
}) {
  return (
    <div className="oct-row">
      {items.map((item) => (
        <div key={item.id} className="oct-col">
          {/* connector bar (horizontal + vertical drop) */}
          <div className="oct-conn">
            <div className="oct-hl" />
            <div className="oct-vl" />
            <div className="oct-hr" />
          </div>
          {renderNode(item, item.id === selectedId)}
        </div>
      ))}
    </div>
  );
}

// ─── page ─────────────────────────────────────────────────────────────────────

export function GeoTreePage() {
  const { store, loading: geoLoading } = useGeoDirectory();
  const { applications } = useAppState();

  const [leads, setLeads]           = useState<LeadTableRow[]>([]);
  const [leadsLoading, setLoading]  = useState(true);
  const [selDistrict, setSelDist]   = useState<DistrictNode | null>(null);
  const [selMandal, setSelMandal]   = useState<MandalNode | null>(null);
  const [search, setSearch]         = useState("");
  const [addLeadTarget, setAddLeadTarget] = useState<AddLeadTarget | null>(null);

  function reloadLeads() {
    fetchLeadsTable().then(setLeads).catch(() => setLeads([]));
  }

  useEffect(() => {
    fetchLeadsTable()
      .then(setLeads)
      .catch(() => setLeads([]))
      .finally(() => setLoading(false));
  }, []);

  const allDistricts = useMemo(() => (store ? store.tgRegistrationDistricts() : []), [store]);
  const tree = useMemo(() => buildTree(allDistricts, leads, applications), [allDistricts, leads, applications]);

  const visibleTree = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tree;

    const result: DistrictNode[] = [];

    for (const d of tree) {
      // district name matches → include whole district
      if (d.name.toLowerCase().includes(q)) {
        result.push(d);
        continue;
      }

      // check mandals
      const matchedMandals: MandalNode[] = [];
      for (const m of d.mandals) {
        if (m.name.toLowerCase().includes(q)) {
          // mandal name matches → include whole mandal
          matchedMandals.push(m);
        } else {
          // check villages inside this mandal
          const matchedVillages = m.villages.filter((v) =>
            v.name.toLowerCase().includes(q),
          );
          if (matchedVillages.length > 0) {
            matchedMandals.push({ ...m, villages: matchedVillages });
          }
        }
      }

      if (matchedMandals.length > 0) {
        result.push({ ...d, mandals: matchedMandals });
      }
    }

    return result;
  }, [tree, search]);

  const stateCounts = useMemo(
    () => ({ leads: leads.length, applicants: applications.length, beneficiaries: applications.filter((a) => a.status === "subcontractor_linked").length }),
    [leads, applications],
  );

  function handleSearch(val: string) {
    setSearch(val);
    const q = val.trim().toLowerCase();

    if (!q) { setSelDist(null); setSelMandal(null); return; }

    for (const d of tree) {
      // district name hit — just show districts row, no auto-expand
      if (d.name.toLowerCase().includes(q)) {
        setSelDist(null); setSelMandal(null); return;
      }
      for (const m of d.mandals) {
        // mandal name hit — auto-expand district so mandals row appears
        if (m.name.toLowerCase().includes(q)) {
          setSelDist(d); setSelMandal(null); return;
        }
        // village name hit — auto-expand district AND mandal so villages row appears
        const vHit = m.villages.some((v) => v.name.toLowerCase().includes(q));
        if (vHit) {
          setSelDist(d); setSelMandal(m); return;
        }
      }
    }
    setSelDist(null); setSelMandal(null);
  }

  function clickDistrict(d: DistrictNode) {
    if (selDistrict?.id === d.id) { setSelDist(null); setSelMandal(null); }
    else { setSelDist(d); setSelMandal(null); }
  }

  function clickMandal(m: MandalNode) {
    if (selMandal?.id === m.id) setSelMandal(null);
    else setSelMandal(m);
  }

  if (geoLoading || leadsLoading) {
    return (
      <div style={{ padding: "4rem", textAlign: "center", color: "#94a3b8" }}>
        Loading…
      </div>
    );
  }

  return (
    <>
      <style>{CONNECTOR_CSS}</style>

      {/* outer: clips and scrolls; inner: grows to content width and centres it */}
      <div style={{ padding: "2rem 0 4rem", overflowX: "auto" }}>
      <div style={{ display: "inline-block", minWidth: "100%", textAlign: "center", padding: "0 2rem" }}>

        {/* page header */}
        <div style={{ marginBottom: "2rem", textAlign: "left" }}>
          <h1 style={{ fontSize: "1.15rem", fontWeight: 700, margin: "0 0 .25rem", color: "#1e293b" }}>
            Area-wise Tree View
          </h1>
          <p style={{ fontSize: ".8rem", color: "#64748b", margin: "0 0 .75rem" }}>
            Click a district to see its mandals · click a mandal to see its villages
          </p>
          <input
            type="search"
            placeholder="🔍  Search district, mandal or village…"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            style={{
              padding: "0.4rem 0.8rem",
              border: "1.5px solid #cbd5e1",
              borderRadius: "7px",
              fontSize: "0.875rem",
              width: "240px",
              outline: "none",
              background: "#fff",
              color: "#1e293b",
            }}
          />
          {search && (
            <span style={{ marginLeft: "0.5rem", fontSize: "0.8rem", color: "#64748b" }}>
              {visibleTree.length} district{visibleTree.length !== 1 ? "s" : ""} found
            </span>
          )}
        </div>

        {/* ══ STATE ══ */}
        <div style={{ display: "flex", justifyContent: "center", textAlign: "left" }}>
          <NodeBox
            name="Telangana"
            icon="🏛️"
            sub={`${visibleTree.length} of ${tree.length} Districts`}
            counts={stateCounts}
            level="state"
          />
        </div>

        {/* stem: state → districts */}
        <div className="oct-stem" />

        {/* ══ DISTRICTS ══ */}
        <OrgRow
          items={visibleTree}
          selectedId={selDistrict?.id}
          renderNode={(d, active) => (
            <NodeBox
              name={d.name}
              icon={active ? "📂" : "📁"}
              sub={`${d.mandals.length} Mandals`}
              counts={sumCounts(d.mandals.flatMap((m) => m.villages))}
              level="district"
              active={active}
              onClick={() => clickDistrict(d)}
            />
          )}
        />

        {/* ══ MANDALS — use filtered district from visibleTree so search narrows the list ══ */}
        {selDistrict && (() => {
          const filteredDist = visibleTree.find((d) => d.id === selDistrict.id) ?? selDistrict;
          if (filteredDist.mandals.length === 0) return null;
          return (
            <>
              <div className="oct-stem" />
              <div style={{
                fontSize: ".7rem", fontWeight: 700, letterSpacing: ".06em",
                textTransform: "uppercase", color: "#94a3b8",
                textAlign: "center", marginBottom: 6,
              }}>
                {filteredDist.name}
              </div>
              <OrgRow
                items={filteredDist.mandals}
                selectedId={selMandal?.id}
                renderNode={(m, active) => (
                  <NodeBox
                    name={m.name}
                    icon={active ? "📂" : "📁"}
                    sub={`${m.villages.length} Villages`}
                    counts={sumCounts(m.villages)}
                    level="mandal"
                    active={active}
                    onClick={() => clickMandal(m)}
                  />
                )}
              />
            </>
          );
        })()}

        {/* ══ VILLAGES — use filtered mandal from visibleTree so search narrows the list ══ */}
        {selDistrict && selMandal && (() => {
          const filteredDist = visibleTree.find((d) => d.id === selDistrict.id) ?? selDistrict;
          const filteredMandal = filteredDist.mandals.find((m) => m.id === selMandal.id) ?? selMandal;
          if (filteredMandal.villages.length === 0) return null;
          return (
            <>
              <div className="oct-stem" />
              <div style={{
                fontSize: ".7rem", fontWeight: 700, letterSpacing: ".06em",
                textTransform: "uppercase", color: "#94a3b8",
                textAlign: "center", marginBottom: 6,
              }}>
                {filteredMandal.name}
              </div>
              <OrgRow
                items={filteredMandal.villages}
                renderNode={(v) => (
                  <div style={{ position: "relative", display: "inline-block" }}>
                    <NodeBox
                      name={v.name}
                      icon="🏘️"
                      counts={v.counts}
                      level="village"
                    />
                    {/* Add Lead button */}
                    <button
                      type="button"
                      title="Add Lead for this village"
                      onClick={() => setAddLeadTarget({
                        district: selDistrict!.name,
                        mandal: selMandal!.name,
                        village: v.name,
                      })}
                      style={{
                        position: "absolute", bottom: -12, left: "50%",
                        transform: "translateX(-50%)",
                        width: 26, height: 26, borderRadius: "50%",
                        background: "#16a34a", border: "2px solid #fff",
                        color: "#fff", fontSize: "1rem", fontWeight: 900,
                        cursor: "pointer", lineHeight: 1,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                      }}
                    >
                      +
                    </button>
                  </div>
                )}
              />
            </>
          );
        })()}

      </div>{/* end inner inline-block */}
      </div>{/* end outer scroll wrapper */}

      {addLeadTarget && (
        <AddLeadModal
          target={addLeadTarget}
          onClose={() => setAddLeadTarget(null)}
          onSaved={reloadLeads}
        />
      )}
    </>
  );
}
